import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import { InvestmentPackage, PackageProject, PackageStats } from 'api-client';
import { createInvestmentPackageManager, createStatsProvider } from 'di';
import { useErrorCallback } from 'hooks';

import { AccountDetailsLoading } from '../shared/AccountDetailsLoading';
import { AccountStatsCard } from '../shared/AccountStatsCard';
import { formatRoiIrr } from '../shared/packageUtils';
import { PackageDealsProgress } from './PackageDealsProgress';
import styles from './PackageDetails.module.scss';
import { PackageDetailsBreadcrumbs } from './PackageDetailsBreadcrumbs';
import { PackageDetailsHeader } from './PackageDetailsHeader';
import { PackageProjectsTable } from './PackageProjectsTable';

export const PackageDetails: FC = () => {
  const { packageUuid } = useParams();
  const handleError = useErrorCallback();
  const handleErrorRef = useRef(handleError);

  const [pkg, setPkg] = useState<InvestmentPackage | null>(null);
  const [stats, setStats] = useState<PackageStats | null>(null);
  const [projects, setProjects] = useState<PackageProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    handleErrorRef.current = handleError;
  }, [handleError]);

  useEffect(() => {
    if (!packageUuid) {
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setNotFound(false);

      try {
        const packageManager = await createInvestmentPackageManager();
        const statsProvider = await createStatsProvider();

        const [packageData, packageStatsList, packageProjects] = await Promise.all([
          packageManager.getOne(packageUuid),
          packageManager.getPackageStats(),
          statsProvider.getProjectsByPackage(packageUuid),
        ]);

        if (cancelled) {
          return;
        }

        setPkg(packageData);
        setStats(packageStatsList.find((item) => item.packageUuid === packageUuid) ?? null);
        setProjects(packageProjects);
      } catch (error) {
        if (!cancelled && error instanceof Error) {
          await handleErrorRef.current(error);
          setNotFound(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [packageUuid]);

  const statsItems = useMemo(() => {
    if (!pkg) {
      return [
        { label: 'БОНУСОВ НАЧИСЛЕНО', value: '—' },
        { label: 'ИНВЕСТИРОВАНО', value: '—' },
        { label: 'ВОЗВРАЩЕНО', value: '—' },
        { label: 'ПРОГНОЗ ВОЗВРАТА', value: '—' },
        {
          label: 'ФАКТ ROI / IRR',
          value: '— / —',
          tooltip: 'Фактическая доходность по пакету',
        },
      ];
    }

    if (!stats) {
      return [
        { label: 'БОНУСОВ НАЧИСЛЕНО', money: pkg.accruedBonuses },
        { label: 'ИНВЕСТИРОВАНО', value: '—' },
        { label: 'ВОЗВРАЩЕНО', value: '—' },
        { label: 'ПРОГНОЗ ВОЗВРАТА', value: '—' },
        {
          label: 'ФАКТ ROI / IRR',
          value: '— / —',
          tooltip: 'Фактическая доходность по пакету',
        },
      ];
    }

    return [
      { label: 'БОНУСОВ НАЧИСЛЕНО', money: pkg.accruedBonuses },
      { label: 'ИНВЕСТИРОВАНО', money: stats.invested },
      { label: 'ВОЗВРАЩЕНО', money: stats.incomePaid },
      {
        label: 'ПРОГНОЗ ВОЗВРАТА',
        money: stats.forecastReturn ?? { amount: 0, currencyCode: stats.invested.currencyCode },
      },
      {
        label: 'ФАКТ ROI / IRR',
        value: formatRoiIrr(stats.roi, stats.xirr),
        tooltip: 'Фактическая доходность по пакету',
      },
    ];
  }, [pkg, stats]);

  if (!packageUuid) {
    return <Navigate to="../../" replace relative="path" />;
  }

  if (loading) {
    return <AccountDetailsLoading />;
  }

  if (!pkg || notFound) {
    return <Navigate to="../../" replace relative="path" />;
  }

  return (
    <div className={styles.container}>
      <PackageDetailsBreadcrumbs packageUuid={pkg.uuid} />
      <PackageDetailsHeader pkg={pkg} />
      <PackageDealsProgress projectsCount={projects.length} />
      <AccountStatsCard stats={statsItems} />
      <PackageProjectsTable projects={projects} emptyMessage="Нет проектов в пакете" />
    </div>
  );
};
